import { setApiClient } from '@ipfabric/api-client/apiClient';
import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { setGlobalStateDispatch } from 'components/GlobalState/globalStateDispatch';
import { useLocale } from 'containers/App/LocaleContext';
import { createAllRoutes } from 'containers/App/Routes/createAllRoutes';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { useCurrentSnapshotCloudLicensing } from 'pages/SnapshotManagement/useCurrentSnapshotCloudLicensing';
import { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIpfLocation } from 'utils/history/useIpfLocation';
import { useNavigatePushPath } from 'utils/history/useNavigatePushPath';
import { useCommandModel } from 'utils/hooks/useCommandModel';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApiWithoutGlobalState } from 'utils/request/useApi';
import {
  type GlobalStateAdditionalCommandContext,
  type GlobalStateDispatch,
  type GlobalStateModel,
  type GlobalStateOuterState,
  type GlobalStateState,
  globalStateInitialState,
} from './GlobalStateModel';

export const useGlobalState = (): GlobalStateModel => {
  const { showNotification } = useNotifications();
  const msg = useLocale();
  const location = useIpfLocation();
  const navigate = useNavigate();
  const navigatePushPath = useNavigatePushPath();
  const reduxDispatch = useDispatch();
  const reduxStore = useStore();
  const snapshotId = useSnapshotId();
  const cloudLicensingSupported = useCurrentSnapshotCloudLicensing();

  const globalDispatch = useStableCallback<GlobalStateDispatch>(
    (...args: [any, ...any]) => {
      if (!model?.dispatch) {
        throw new IpfError(
          'INVALID_STATE',
          'Trying to use GlobalState.dispatch before it was initialized',
        );
      }
      return model.dispatch(...args);
    },
  );

  const api = useApiWithoutGlobalState({ globalDispatch });

  const model: GlobalStateModel = useCommandModel<
    GlobalStateState,
    GlobalStateAdditionalCommandContext,
    GlobalStateOuterState
  >(
    globalStateInitialState,
    {
      api,
      logError: useLogError(),
      navigate,
      navigatePushPath,
      reduxDispatch,
      reduxStore,
      showNotification,
    },
    {
      createAllRoutes,
      cloudLicensingSupported,
      location,
      msg,
      snapshotId,
    },
  );

  useEffect(() => {
    setGlobalStateDispatch(model.dispatch);
    setApiClient((config) => {
      return api.request({
        url: config.url,
        method: config.method,
        data: config.data,
      });
    });

    return () => {
      setGlobalStateDispatch(null);
      setApiClient(null);
    };
  }, [model.dispatch, api.request]);

  return model;
};
